// ---------------------------------------------------------------------------
// cogibulle diffusion — Achat in-app du mode embarqué (appli native)
//
// Débloque l'« accès complet » (produit non consommable unique décrit dans
// app.json, voir exports_app/<slug>/app.json) derrière une gate parentale,
// exigence de la catégorie Enfants des stores. Trois surfaces :
//   - gate parentale : petite multiplication à résoudre avant tout commerce ;
//   - écran d'achat : bouton Acheter (avec prix du store quand disponible)
//     et bouton « Restaurer mes achats » (obligatoire chez Apple) ;
//   - intégration cordova-plugin-purchase (StoreKit / Play Billing) quand
//     l'appli tourne dans Cordova ; en navigateur (workspace de test), un
//     mode simulation local permet de vérifier tout le parcours.
//
// L'état persistant vit dans runtime_embarque.js (definit_achats_embarque →
// localStorage) : le déverrouillage réagit immédiatement et survit au
// redémarrage, hors ligne.
// ---------------------------------------------------------------------------

import { Meteor } from 'meteor/meteor';
import { Template } from 'meteor/templating';
import { ReactiveVar } from 'meteor/reactive-var';

import {
  est_runtime_embarque,
  app_embarque,
  definit_achats_embarque,
} from '../runtime_embarque.js';

import './achat_embarque.html';
import './achat_embarque.css';

const gAchatOuvert = new ReactiveVar (false);
const gAchatFermeture = new ReactiveVar (false);
const gAchatEtape = new ReactiveVar ('gate');      // 'gate' | 'achat'
const gAchatBusy = new ReactiveVar (false);
const gAchatMessage = new ReactiveVar (null);      // {texte, succes}
const gAchatPrix = new ReactiveVar (null);         // libellé prix du store
const gGateQuestion = new ReactiveVar (null);      // {a, b}
// Android : la WebView plein écran ne se redimensionne pas quand le clavier
// s'ouvre, il recouvrirait le champ et les boutons. Pendant la saisie, la carte
// se compacte en haut de l'écran (voir .achat_embarque_clavier).
const gGateClavier = new ReactiveVar (false);
const CLAVIER_RECOUVRANT = Meteor.isCordova && /Android/i.test (navigator.userAgent);
let gGateClavierTimer = null;

let gStoreCordovaInitialise = false;
let gGateApresReussite = null;                     // gate seule : suite à donner

function produit_acces_complet ()
  {
    const tApp = app_embarque ();
    const tProduits = tApp && tApp.monetisation && tApp.monetisation.produits;
    return (Array.isArray (tProduits) && tProduits[0]) || null;
  }

function nouvelle_question_gate ()
  {
    const tA = 3 + Math.floor (Math.random () * 7);   // 3..9
    const tB = 4 + Math.floor (Math.random () * 6);   // 4..9
    gGateQuestion.set ({a : tA, b : tB});
  }

export function ouvre_achat_embarque ()
  {
    if (!est_runtime_embarque ()) return false;
    gGateApresReussite = null;
    nouvelle_question_gate ();
    gAchatEtape.set ('gate');
    gAchatMessage.set (null);
    gAchatBusy.set (false);
    gAchatFermeture.set (false);
    gAchatOuvert.set (true);
    return true;
  }

// Gate parentale seule (sans écran d'achat) : pCallback est appelé une fois la
// multiplication résolue. Sert aux liens qui sortent de l'appli.
export function ouvre_gate_parentale_embarque (pCallback)
  {
    if (!ouvre_achat_embarque ()) return false;
    gGateApresReussite = typeof pCallback === 'function' ? pCallback : null;
    return true;
  }

// Ouvre une URL hors de l'appli. Dans Cordova, la navigation vers un domaine
// déclaré en « intent » (mobile-config.js) est confiée au navigateur du
// système et la WebView ne bouge pas ; au navigateur (tests), nouvel onglet.
export function ouvre_url_externe_embarque (pUrl)
  {
    if (!pUrl) return false;
    if (Meteor.isCordova) window.location.assign (pUrl);
    else window.open (pUrl, '_blank', 'noopener,noreferrer');
    return true;
  }

function ferme_achat_embarque ()
  {
    if (!gAchatOuvert.get () || gAchatFermeture.get ()) return;
    gAchatFermeture.set (true);
    setTimeout (() =>
      {
        gAchatOuvert.set (false);
        gAchatFermeture.set (false);
        gGateClavier.set (false);
      }, 420);
  }

function enregistre_acces_complet (pMessage)
  {
    definit_achats_embarque ({acces_complet : true});
    gAchatBusy.set (false);
    gAchatMessage.set ({texte : pMessage, succes : true});
    setTimeout (ferme_achat_embarque, 1600);
  }

// ---------------------------------------------------------------------------
// Store natif (cordova-plugin-purchase v13) — uniquement dans Cordova.
// Sans validateur de reçus distant (appli hors ligne) : on suit l'état
// « owned » local du store, qui suffit pour un non consommable.
// ---------------------------------------------------------------------------
function store_cordova ()
  {
    return (typeof window !== 'undefined'
      && window.CdvPurchase
      && window.CdvPurchase.store) || null;
  }

function initialise_store_cordova ()
  {
    if (gStoreCordovaInitialise) return;
    const tStore = store_cordova ();
    const tProduit = produit_acces_complet ();
    if (!tStore || !tProduit) return;
    gStoreCordovaInitialise = true;

    const CdvPurchase = window.CdvPurchase;
    const tPlateformes = [];
    if (CdvPurchase.Platform.APPLE_APPSTORE) tPlateformes.push (CdvPurchase.Platform.APPLE_APPSTORE);
    if (CdvPurchase.Platform.GOOGLE_PLAY) tPlateformes.push (CdvPurchase.Platform.GOOGLE_PLAY);

    for (const tPlateforme of tPlateformes)
      tStore.register (
        {
          id : tProduit.id,
          type : CdvPurchase.ProductType.NON_CONSUMABLE,
          platform : tPlateforme,
        }
      );

    tStore.when ()
      .approved ((pTransaction) => pTransaction.finish ())
      .finished (() =>
        {
          if (tStore.owned (tProduit.id))
            enregistre_acces_complet ('Merci ! Toutes les scènes sont débloquées.');
        })
      .productUpdated ((pProduct) =>
        {
          if (pProduct.id !== tProduit.id) return;
          const tOffre = pProduct.getOffer && pProduct.getOffer ();
          const tPrix = tOffre && tOffre.pricingPhases && tOffre.pricingPhases[0]
            && tOffre.pricingPhases[0].price;
          if (tPrix) gAchatPrix.set (tPrix);
          if (tStore.owned (tProduit.id))
            definit_achats_embarque ({acces_complet : true});
        });

    tStore.error ((pErr) =>
      {
        console.warn ('[achat] erreur store', pErr);
        gAchatBusy.set (false);
        gAchatMessage.set ({texte : 'Le magasin d’applications n’a pas répondu. Réessayez plus tard.'});
      });

    tStore.initialize (tPlateformes);
  }

function lance_achat ()
  {
    gAchatMessage.set (null);
    const tProduit = produit_acces_complet ();
    if (!tProduit) return;

    const tStore = store_cordova ();
    if (tStore)
      {
        const tProduct = tStore.get (tProduit.id);
        const tOffre = tProduct && tProduct.getOffer && tProduct.getOffer ();
        if (!tOffre)
          {
            gAchatMessage.set ({texte : 'Produit indisponible pour le moment. Vérifiez votre connexion et réessayez.'});
            return;
          }
        gAchatBusy.set (true);
        tOffre.order ().then ((pErr) =>
          {
            if (pErr)
              {
                gAchatBusy.set (false);
                if (!pErr.isCanceled)
                  gAchatMessage.set ({texte : 'L’achat n’a pas abouti. Réessayez.'});
                else
                  gAchatMessage.set (null);
              }
            // Succès : géré par approved → finished ci-dessus.
          });
        return;
      }

    // Navigateur (workspace de test) : simulation locale du parcours complet.
    gAchatBusy.set (true);
    console.info ('[achat] simulation navigateur : achat accordé dans 1 s');
    setTimeout (() => enregistre_acces_complet ('Achat simulé : toutes les scènes sont débloquées.'), 1000);
  }

function lance_restauration ()
  {
    gAchatMessage.set (null);
    const tStore = store_cordova ();
    if (tStore)
      {
        gAchatBusy.set (true);
        tStore.restorePurchases ().then (() =>
          {
            gAchatBusy.set (false);
            const tProduit = produit_acces_complet ();
            if (tProduit && tStore.owned (tProduit.id))
              enregistre_acces_complet ('Achat restauré : toutes les scènes sont débloquées.');
            else
              gAchatMessage.set ({texte : 'Aucun achat à restaurer sur ce compte.'});
          });
        return;
      }

    // Navigateur : la restauration relit simplement l'état persisté local.
    gAchatMessage.set ({texte : 'Aucun achat à restaurer (simulation navigateur).'});
  }

Meteor.startup (() =>
  {
    if (!est_runtime_embarque ()) return;
    if (Meteor.isCordova)
      document.addEventListener ('deviceready', initialise_store_cordova, false);
  });

Template.diffusionAchatEmbarque.helpers (
  {
    achat_dialog_open ()
      {
        return gAchatOuvert.get ();
      },

    achat_dialog_class ()
      {
        if (gAchatFermeture.get ()) return 'is-closing';
        if (!gAchatOuvert.get ()) return '';
        return gGateClavier.get () ? 'is-open achat_embarque_clavier' : 'is-open';
      },

    achat_etape_gate ()
      {
        return gAchatEtape.get () === 'gate';
      },

    achat_gate_question ()
      {
        const tQ = gGateQuestion.get ();
        return tQ ? `${tQ.a} × ${tQ.b}` : '';
      },

    achat_titre ()
      {
        const tApp = app_embarque ();
        return tApp && tApp.nom_affichage
          ? `${tApp.nom_affichage} — version complète`
          : 'Version complète';
      },

    achat_description ()
      {
        const tApp = app_embarque ();
        const tNb = tApp && tApp.monetisation && tApp.monetisation.nb_scenes_payantes;
        if (Number.isFinite (tNb) && tNb > 0)
          return `Débloquez les ${tNb} scènes supplémentaires, en un seul achat, pour toujours.`;
        return 'Débloquez toutes les scènes, en un seul achat, pour toujours.';
      },

    achat_bouton_label ()
      {
        const tPrix = gAchatPrix.get ();
        return tPrix ? `Acheter — ${tPrix}` : 'Acheter';
      },

    achat_busy_attr ()
      {
        return gAchatBusy.get () ? 'disabled' : null;
      },

    achat_message ()
      {
        const tMessage = gAchatMessage.get ();
        return tMessage && tMessage.texte;
      },
  });

Template.diffusionAchatEmbarque.events (
  {
    'focusin .js_achat_gate_reponse' ()
      {
        if (!CLAVIER_RECOUVRANT) return;
        clearTimeout (gGateClavierTimer);
        gGateClavier.set (true);
      },

    // Retour différé : toucher « Continuer » fait d'abord perdre le focus au
    // champ, et le bouton ne doit pas bouger sous le doigt avant le clic.
    'focusout .js_achat_gate_reponse' ()
      {
        if (!CLAVIER_RECOUVRANT) return;
        clearTimeout (gGateClavierTimer);
        gGateClavierTimer = setTimeout (() => gGateClavier.set (false), 400);
      },

    'submit .js_achat_gate_form' (pEvent, pTpl)
      {
        pEvent.preventDefault ();
        const tChamp = pTpl.find ('.js_achat_gate_reponse');
        const tQ = gGateQuestion.get ();
        const tReponse = tChamp ? Number (tChamp.value) : NaN;
        if (tQ && tReponse === tQ.a * tQ.b)
          {
            gAchatMessage.set (null);
            if (gGateApresReussite)
              {
                const tSuite = gGateApresReussite;
                gGateApresReussite = null;
                ferme_achat_embarque ();
                tSuite ();
                return;
              }
            gAchatEtape.set ('achat');
            return;
          }
        nouvelle_question_gate ();
        if (tChamp) tChamp.value = '';
        gAchatMessage.set ({texte : 'Ce n’est pas la bonne réponse. Nouvelle question !'});
      },

    'click .js_achat_acheter' ()
      {
        if (!gAchatBusy.get ()) lance_achat ();
      },

    'click .js_achat_restaurer' ()
      {
        if (!gAchatBusy.get ()) lance_restauration ();
      },

    'click .js_achat_fermer' ()
      {
        ferme_achat_embarque ();
      },
  });
